import { createSlice } from '@reduxjs/toolkit';
import { fpLogging, isOwner } from '../../../shared/utils';
import win from '../../../shared/win';
import { resolveLegacyRoles } from '../utils';

const defaultState = {
  activeTeam: win.ACTIVE_TEAM || {},
  activeName: win.ACTIVE_NAME || '',
  joined: win.TEAMS || {},
  inviteeProcessIds: {},
  memberProcessIds: {},
  isLoadingForms: true,
  teamsLoaded: false,
  formProcessIds: {},
  teamLoaded: false,
  formsError: false,
  currentTeam: {},
  isLoading: true,
  teamsError: '',
  teamError: '',
  owned: {},

  formList: {
    total_responses: 0,
    all_keys: [],
    added: {},
    left: {},
  },
};

const teamsManagerSlice = createSlice({
  name: 'teams',
  initialState: defaultState,
  reducers: {
    fetchAllTeamsStart: (state) => {
      state.teamsError = '';
      state.isLoading = true;
    },
    fetchAllTeamsFail: (state, { payload }) => {
      state.isLoading = false;
      state.teamsError = payload.error;
      fpLogging().logErrorToCloudReporting(payload.error);
    },
    fetchAllTeamsSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.teamsLoaded = true;
      state.owned = payload.list;
    },
    fetchTeamStart: (state) => {
      state.teamsError = '';
      state.teamError = '';
      state.isLoading = true;
      state.currentTeam = {};
      state.memberProcessIds = {};
    },
    fetchTeamFail: (state, { payload }) => {
      state.isLoading = false;
      state.teamError = payload.error;
      fpLogging().logErrorToCloudReporting(payload.error);
    },
    fetchTeamSuccess: (state, { payload }) => {
      state.isLoading = false;
      const { team } = payload;
      const resolvedMembers = {};

      for (const memberId in team.members) {
        const member = team.members[memberId];
        const resolvedRole = resolveLegacyRoles(member.roles);
        resolvedMembers[memberId] = { ...member, roles: resolvedRole };
      }

      state.currentTeam = { ...team, members: resolvedMembers };
      state.teamLoaded = true;
    },
    fetchTeamFormsStart: (state) => {
      state.isLoadingForms = true;
      state.formsError = false;
    },
    fetchTeamFormsSuccess: (state, { payload }) => {
      const { added, left, all_keys, total_responses } = payload;

      const newFormList = {
        added,
        left,
        all_keys,
        total_responses,
      };
      state.formList = newFormList;
      state.isLoadingForms = false;
    },
    fetchTeamFormsFail: (state, { payload }) => {
      state.isLoadingForms = false;
      state.formsError = payload.error;
      fpLogging().logErrorToCloudReporting(payload.error);
    },
    createTeamSuccess: (state, { payload }) => {
      const { newTeam } = payload;
      state.owned[newTeam.id] = newTeam;
      state.immediateJoin = true;
    },
    renameTeamSuccess: (state, { payload }) => {
      const { name } = payload;

      state.currentTeam.name = name;

      if (state.currentTeam.id in state.owned) {
        state.owned[state.currentTeam.id].name = name;
      }

      if (state.currentTeam.id in state.joined) {
        state.joined[state.currentTeam.id].name = name;
      }

      if (state.currentTeam.id === state.activeTeam?.id) {
        state.activeTeam.name = name;
      }
    },
    joinTeamSuccess: (state, { payload }) => {
      const { member } = payload;
      state.immediateJoin = false;

      state.currentTeam.members[member.id] = member;
      state.currentTeam.member_count++;

      if (state.owned[state.currentTeam.id]) {
        state.owned[state.currentTeam.id].member_count++;
      }

      state.joined[state.currentTeam.id] = {
        ...state.currentTeam,
        email: member.email,
        member,
      };
    },
    processMemberStart: (state, { payload }) => {
      const { itemId } = payload;
      state.memberProcessIds[itemId] = true;
    },
    deleteMemberSuccess: (state, { payload }) => {
      const { memberId } = payload;

      delete state.memberProcessIds[memberId];
      delete state.currentTeam.members[memberId];
      state.currentTeam.member_count--;

      // The Actor ==> user performing the deletion
      const actorOwnsTeam = isOwner(state.currentTeam);
      const matchingOwnedTeam = state.owned[state.currentTeam.id];
      const matchingJoinedTeam = state.joined[state.currentTeam.id];
      const deletedMemberIsActor = `${matchingJoinedTeam?.member?.id}` === `${memberId}`;

      // If currentTeam is owned by the actor,
      if (actorOwnsTeam) {
        // Reduce member_count from his matching owned team
        if (matchingOwnedTeam) {
          state.owned[state.currentTeam.id].member_count--;
        }

        // If actor is deleting from a joined team
        if (matchingJoinedTeam) {
          // If actor has deleted himself
          if (deletedMemberIsActor) {
            // Delete his matching joined team
            // Without routing him away since he can still view the team he has left because he owns it
            delete state.joined[state.currentTeam.id];
          } else {
            // Just reduce member_count from his matching joined team
            state.joined[state.currentTeam.id].member_count--;
          }
        }
      }

      // If actor doesnt own current team,
      if (!actorOwnsTeam) {
        // Actor doesnt own team, he has definitely joined the current team
        // If actor is deleting himself
        if (deletedMemberIsActor) {
          // Delete his matching joined team
          // And route him away since he doesnt own team and can no longer view a team he has left
          delete state.joined[state.currentTeam.id];
          state.routeAway = true;
        } else {
          // Just reduce member_count from his matching joined team
          if (matchingJoinedTeam) {
            state.joined[state.currentTeam.id].member_count--;
          }
        }
      }
    },
    deleteMemberFail: (state, { payload }) => {
      const { memberId } = payload;
      delete state.memberProcessIds[memberId];
    },
    updateMemberSuccess: (state, { payload }) => {
      const { member } = payload;
      delete state.memberProcessIds[member.id];
      state.currentTeam.members[member.id] = member;
    },
    updateMemberFail: (state, { payload }) => {
      const { memberId } = payload;
      delete state.memberProcessIds[memberId];
    },
    deleteInviteSuccess: (state, { payload }) => {
      const { inviteId } = payload;

      delete state.memberProcessIds[inviteId];
      delete state.currentTeam.members[inviteId];
      state.currentTeam.member_count--;

      // The Actor ==> user performing the deletion
      const actorOwnsTeam = isOwner(state.currentTeam);
      const matchingOwnedTeam = state.owned[state.currentTeam.id];
      const matchingJoinedTeam = state.joined[state.currentTeam.id];

      if (actorOwnsTeam) {
        if (matchingOwnedTeam) {
          state.owned[state.currentTeam.id].member_count--;
        }

        // If actor is deleting from a joined team
        if (matchingJoinedTeam) {
          state.joined[state.currentTeam.id].member_count--;
        }
      } else {
        if (matchingJoinedTeam) {
          state.joined[state.currentTeam.id].member_count--;
        }
      }
    },
    deleteInviteFail: (state, { payload }) => {
      const { inviteId } = payload;
      delete state.memberProcessIds[inviteId];
    },
    deleteTeamSuccess: (state, { payload }) => {
      const { teamId } = payload;

      if (teamId in state.owned) {
        delete state.owned[teamId];
      }

      if (teamId in state.joined) {
        delete state.joined[teamId];
      }
    },
    processInvitesStart: (state, { payload }) => {
      const { inviteeIds } = payload;

      inviteeIds.forEach((inviteeId) => {
        state.inviteeProcessIds[inviteeId] = true;
      });
    },
    inviteMembersSuccess: (state, { payload }) => {
      const { inviteeIds, invitedMembers } = payload;
      state.currentTeam.members = { ...state.currentTeam.members, ...invitedMembers };

      const matchingJoinedTeam = state.joined[state.currentTeam.id];
      const matchingOwnedTeam = state.owned[state.currentTeam.id];
      const invitedMembersCount = Object.keys(invitedMembers).length;

      state.currentTeam.member_count += invitedMembersCount;

      if (matchingJoinedTeam) {
        state.joined[state.currentTeam.id].member_count += invitedMembersCount;
      }

      if (matchingOwnedTeam) {
        state.owned[state.currentTeam.id].member_count += invitedMembersCount;
      }

      inviteeIds.forEach((inviteeId) => {
        delete state.inviteeProcessIds[inviteeId];
      });
    },
    inviteMembersFail: (state, { payload }) => {
      const { inviteeIds } = payload;

      inviteeIds.forEach((inviteeId) => {
        delete state.inviteeProcessIds[inviteeId];
      });
    },
    resendInviteTried: (state, { payload }) => {
      const { inviteId } = payload;
      delete state.memberProcessIds[inviteId];
    },
    processFormStart: (state, { payload }) => {
      const { formIds } = payload;

      formIds.forEach((formId) => {
        state.formProcessIds[formId] = true;
      });
    },
    formsAddSuccess: (state, { payload }) => {
      const { newForms } = payload;
      state.formList.added = { ...state.formList.added, ...newForms };

      Object.keys(newForms).forEach((formId) => {
        delete state.formList.left[formId];
        delete state.formProcessIds[formId];
      });
    },
    formsRemoveSuccess: (state, { payload }) => {
      const { newForms } = payload;
      state.formList.left = { ...state.formList.left, ...newForms };

      Object.keys(newForms).forEach((formId) => {
        delete state.formList.added[formId];
        delete state.formProcessIds[formId];
      });
    },
    updateFormsFail: (state, { payload }) => {
      const { formIds } = payload;

      formIds.forEach((formId) => {
        delete state.formProcessIds[formId];
      });
    },
    setRouteAway: (state, { payload }) => {
      state.routeAway = payload;
    },
    clearTeamError: (state) => {
      state.teamError = '';
    },
  },
});

const { reducer, actions } = teamsManagerSlice;
export default reducer;
export const {
  fetchTeamFormsSuccess,
  inviteMembersSuccess,
  fetchAllTeamsSuccess,
  updateMemberSuccess,
  processInvitesStart,
  deleteInviteSuccess,
  fetchTeamFormsStart,
  deleteMemberSuccess,
  processMemberStart,
  fetchTeamFormsFail,
  formsRemoveSuccess,
  fetchAllTeamsStart,
  inviteMembersFail,
  resendInviteTried,
  fetchAllTeamsFail,
  deleteTeamSuccess,
  renameTeamSuccess,
  createTeamSuccess,
  deleteMemberFail,
  updateMemberFail,
  deleteInviteFail,
  fetchTeamSuccess,
  processFormStart,
  updateFormsFail,
  formsAddSuccess,
  joinTeamSuccess,
  clearTeamError,
  fetchTeamStart,
  fetchTeamFail,
  setRouteAway,
} = actions;
